import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PosExpiredCard from './PosExpiredCard';
import PosSubscribeModal from './PosSubscribeModal';
import { POS_EXPIRED_WATCHDOG_MS } from '../constants/posConst';
import '../css/PosSubscription.css';

/** The app's root element, which is frozen while the lock is up. */
const APP_ROOT_ID = 'root';

/**
 * The styles the lock needs to actually cover the till. Re-applied inline if
 * anything (developer tools, an extension, a deleted stylesheet) leaves the
 * lock hidden, transparent or out of place.
 */
const FORCED_STYLE = {
  position: 'fixed',
  inset: '0',
  display: 'flex',
  visibility: 'visible',
  opacity: '1',
  'z-index': '1150',
  'pointer-events': 'auto',
};

/**
 * Whether the lock element is on screen and covering the page.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
const isCovering = (element) => {
  const style = window.getComputedStyle(element);
  const box = element.getBoundingClientRect();
  return style.display !== 'none'
    && style.visibility === 'visible'
    && Number(style.opacity) >= 0.95
    && style.position === 'fixed'
    && box.width >= window.innerWidth - 1
    && box.height >= window.innerHeight - 1;
};

/**
 * Covers a till whose plan or free trial has EXPIRED — automatically, with no
 * administrator involved.
 *
 * A popup over whatever screen the club was on, which stays visible but
 * blurred and frozen behind it. It is mounted only AFTER the club has had
 * POS_EXPIRED_GRACE_MS of normal use (see PosPage), so it appears over the
 * last screen they viewed rather than greeting them on arrival.
 *
 * THERE IS NO CLOSE. The way out is to renew (the Subscribe dialog opens on
 * top of this) or for an administrator to extend the plan.
 *
 * IT PUTS ITSELF BACK. Anyone can delete an element or hide it with developer
 * tools, so a watchdog — a MutationObserver plus a timer — checks that the
 * lock is still in the page, still covering it, and that the till behind is
 * still frozen, and restores whichever was undone. It restores the SAME DOM
 * node rather than asking React to re-render, because React removing a node
 * someone else already removed throws.
 *
 * This is a deterrent, not security: the data rules still admit any signed-in
 * account (see the honest-limit note in database.rules.json).
 *
 * @returns {JSX.Element}
 */
function PosExpiredLock() {
  const [renewing, setRenewing] = useState(false);
  const lockRef = useRef(null);

  // Its own container on <body>, outside the app root — so freezing the app
  // (inert) does not freeze the lock, and the container can be re-attached
  // if removed.
  const container = useMemo(() => {
    const element = document.createElement('div');
    element.setAttribute('data-ps-lock-root', '');
    return element;
  }, []);

  useEffect(() => {
    document.body.appendChild(container);
    return () => container.remove();
  }, [container]);

  // Freeze the till behind: no clicks, no typing, no focus. Undone on unmount,
  // which is what happens the moment a renewal is approved.
  useEffect(() => {
    const root = document.getElementById(APP_ROOT_ID);
    if (!root) return undefined;
    root.inert = true;
    root.setAttribute('aria-hidden', 'true');
    return () => {
      root.inert = false;
      root.removeAttribute('aria-hidden');
    };
  }, []);

  // The watchdog.
  useEffect(() => {
    let checking = false;

    /** Restores anything that was removed, hidden or unfrozen. @returns {void} */
    const check = () => {
      if (checking) return;
      checking = true;
      try {
        if (!document.body.contains(container)) document.body.appendChild(container);

        const lock = lockRef.current;
        if (lock) {
          if (!container.contains(lock)) container.appendChild(lock);
          ['hidden', 'inert'].forEach((attribute) => {
            if (lock.hasAttribute(attribute)) lock.removeAttribute(attribute);
          });
          if (!lock.classList.contains('ps-lock-modal')) lock.classList.add('ps-lock', 'ps-lock-float', 'ps-lock-modal');
          if (!isCovering(lock)) {
            Object.entries(FORCED_STYLE).forEach(([key, value]) => lock.style.setProperty(key, value, 'important'));
          }
        }

        const root = document.getElementById(APP_ROOT_ID);
        if (root && !root.inert) root.inert = true;
      } finally {
        checking = false;
      }
    };

    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'inert'],
    });
    const timer = setInterval(check, POS_EXPIRED_WATCHDOG_MS);
    return () => {
      observer.disconnect();
      clearInterval(timer);
    };
  }, [container]);

  return createPortal(
    <>
      <div
        ref={lockRef}
        className="ps-lock ps-lock-float ps-lock-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Subscription expired"
      >
        <PosExpiredCard onRenew={() => setRenewing(true)} />
      </div>
      <PosSubscribeModal isOpen={renewing} onClose={() => setRenewing(false)} />
    </>,
    container,
  );
}

export default PosExpiredLock;
